import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { normalizeEmail } from '@/lib/authErrorMessage';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

const isRetryableNetworkError = (error) => !error?.response && (error?.code === 'ECONNABORTED' || error?.message === 'Network Error');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('clientLastActivity');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email: normalizeEmail(email), password },
      { headers: JSON_HEADERS },
    );
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('clientLastActivity', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const payload = { name: name.trim(), email: normalizeEmail(email), password };
    let response;

    try {
      response = await axios.post(`${API}/auth/register`, payload, { headers: JSON_HEADERS });
    } catch (error) {
      if (!isRetryableNetworkError(error)) {
        throw error;
      }
      response = await axios.post(`${API}/auth/register`, payload, { headers: JSON_HEADERS });
    }

    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('clientLastActivity', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('clientLastActivity');
    setToken(null);
    setUser(null);
  };

  const updateFavorites = (favorites) => {
    setUser(prev => ({ ...prev, favorites }));
  };

  const updateUserProfile = (profileData) => {
    setUser(prev => ({ ...prev, ...profileData }));
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateFavorites,
    updateUserProfile,
    refreshUser: fetchUser,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
